import React, { FC } from 'react';
import { Alert, Box, Button, CircularProgress, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { assistantConversationsTemplate } from 'apollo/templates/assistant';
import { AssistantConversationSummary } from 'models/assistant';

type AssistantHistoryProps = {
    onOpenConversation: (conversationId: string) => void;
};

/** DD-3780: the user's past assistant conversations (90 days), newest first. */
const AssistantHistory: FC<AssistantHistoryProps> = ({ onOpenConversation }: AssistantHistoryProps) => {
    const { t: translate } = useTranslation(['help-drawer']);
    const { data, loading, error, refetch } = useQuery<{ assistantConversations: AssistantConversationSummary[] }>(
        assistantConversationsTemplate,
        { fetchPolicy: 'cache-and-network' },
    );
    const conversations = data?.assistantConversations ?? [];

    if (error) {
        return (
            <Alert
                severity="error"
                action={
                    <Button color="inherit" size="small" onClick={() => refetch()}>
                        {translate('help-drawer:RETRY')}
                    </Button>
                }
            >
                {translate('help-drawer:ASSISTANT_HISTORY_FAILED')}
            </Alert>
        );
    }
    return (
        <Box>
            <Typography variant="body2" color="grey" my={1}>
                {translate('help-drawer:ASSISTANT_HISTORY_INTRO')}
            </Typography>
            {loading && !data ? <CircularProgress size={24} /> : undefined}
            {!loading && !conversations.length ? (
                <Typography variant="body2" my={2}>
                    {translate('help-drawer:ASSISTANT_HISTORY_EMPTY')}
                </Typography>
            ) : undefined}
            <List disablePadding>
                {conversations.map((conversation) => (
                    <ListItemButton key={conversation._id} divider onClick={() => onOpenConversation(conversation._id)}>
                        <ListItemText
                            primary={conversation.title}
                            secondary={`${format(new Date(conversation.updatedAt), 'yyyy-MM-dd HH:mm')} · ${translate(
                                'help-drawer:ASSISTANT_HISTORY_MESSAGES',
                                { count: conversation.messageCount },
                            )}`}
                        />
                    </ListItemButton>
                ))}
            </List>
        </Box>
    );
};

export default AssistantHistory;
